// MaturaLinearInequalityClosedTask.js
// Zadanie maturalne zamknięte:  nierówność liniowa typu
//  (wariant Lminus)  a - x  (rel)  (b x + c) / d
//  (wariant Rminus)  x + a  (rel)  (c - b x) / d
//  (wariant FracLeft) (b x + c) / d (rel) x + a
// gdzie d ∈ {2,3,5}, rel ∈ {>=, >, <=, <},
// a odpowiedź to przedział w postaci półprostej.

// ===== IMPORTY =====
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

// ===== Helpers =====
const LETTERS = ["A", "B", "C", "D"];

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function mixedLatex(num, den) {
  let { num: N, den: D } = simplifyFrac(num, den);
  const sign = N < 0 ? "-" : "";
  N = Math.abs(N);
  if (N % D === 0) return sign ? `${sign}${N / D}` : `${N / D}`;
  const A = Math.floor(N / D);
  const R = N % D;
  if (A > 0) return `${sign}${A}\\;\\frac{${R}}{${D}}`;
  return `${sign}\\frac{${R}}{${D}}`;
}

const signNum = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);

function intervalLatex(leftType, tLatex, rightType) {
  // leftType: "open" | "closed" | "-inf"
  // rightType: "open" | "closed" | "+inf"
  if (leftType === "-inf") {
    const rb = rightType === "closed" ? "]" : ")";
    return `(-\\infty,\\; ${tLatex}${rb}`;
  }
  if (rightType === "+inf") {
    const lb = leftType === "closed" ? "[" : "(";
    return `${lb}${tLatex},\\; +\\infty)`;
  }
  const lb = leftType === "closed" ? "[" : "(";
  const rb = rightType === "closed" ? "]" : ")";
  return `${lb}${tLatex},\\; ${tLatex}${rb}`;
}

// kierunek nierówności dla rozwiązania: "<=", "<", ">=", ">"
function intervalFromDirection(direction, tLatex) {
  switch (direction) {
    case "<=":
      return intervalLatex("-inf", tLatex, "closed");
    case "<":
      return intervalLatex("-inf", tLatex, "open");
    case ">=":
      return intervalLatex("closed", tLatex, "+inf");
    case ">":
      return intervalLatex("open", tLatex, "+inf");
    default:
      return "";
  }
}

function flipDirection(direction) {
  switch (direction) {
    case "<=":
      return ">=";
    case ">=":
      return "<=";
    case "<":
      return ">";
    case ">":
      return "<";
    default:
      return direction;
  }
}

const dirToLatex = {
  "<=": "\\le",
  ">=": "\\ge",
  "<": "<",
  ">": ">",
};

// ============================================================================
// GENERATOR
// ============================================================================
/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 7:
 *   3 - x >= (5x - 1)/2   →   A. (-∞, 1]
 * Próg błędny zapisany jako 7/6 (ułamek niewłaściwy, jak w arkuszu CKE).
 */
function createOriginalTask() {
  const variant = "Lminus";
  const a = 3;
  const b = 5;
  const c = -1;
  const d = 2;
  const relKey = ">=";
  const relLatex = "\\ge";
  const exprLatex = "3 - x \\; \\ge \\; \\dfrac{5x - 1}{2}";

  const t_num = 7; // d*a - c = 6 - (-1)
  const t_den = 7; // b + d
  const wrong_num = 7; // d*a - c
  const wrong_den = 6; // b + 1
  const tLatex = "1";
  const tWrongLatex = "\\frac{7}{6}";
  const direction = "<=";
  const directionFlipped = ">=";

  const options = [
    { label: "A", latex: "(-\\infty,\\; 1]", isCorrect: true },
    { label: "B", latex: "(-\\infty,\\; \\frac{7}{6}]", isCorrect: false },
    { label: "C", latex: "[1,\\; +\\infty)", isCorrect: false },
    { label: "D", latex: "[\\frac{7}{6},\\; +\\infty)", isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLinearInequalityClosedTask",
    data: {
      variant,
      a,
      b,
      c,
      d,
      relKey,
      relLatex,
      exprLatex,
      t_num,
      t_den,
      wrong_num,
      wrong_den,
      tLatex,
      tWrongLatex,
      direction,
      directionFlipped,
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 7,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const rnd = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  // wariant konstrukcji
  const variant = ["Lminus", "Rminus", "FracLeft"][rnd(0, 2)];

  // mianownik: 2, 3 lub 5
  let d = [2, 3, 5][rnd(0, 2)];

  // relacja nierówności
  const relOptions = [
    { key: ">=", latex: "\\ge" },
    { key: "<=", latex: "\\le" },
    { key: ">", latex: ">" },
    { key: "<", latex: "<" },
  ];
  const rel = relOptions[rnd(0, relOptions.length - 1)];

  let a, b, c;

  // ---------------- LOSOWANIE PARAMETRÓW WG WARIANTU ----------------

  if (variant === "Lminus") {
    // a - x (rel) (b x + c) / d
    a = rnd(1, 6);
    // współczynnik przy x w ułamku > 1
    b = rnd(2, 6);
    c = rnd(-6, 8);
    if (c === 0) c = 1;
  } else if (variant === "Rminus") {
    // x + a (rel) (c - b x) / d
    a = rnd(1, 6);
    b = rnd(2, 6); // > 1
    c = rnd(1, 8); // dodatnia stała typu "6 - 3x"
  } else {
    // FracLeft: (b x + c) / d (rel) x + a
    a = rnd(1, 6);
    // chcemy b > d i b > 1 -> losujemy aż spełni warunek
    do {
      b = rnd(2, 7); // >1
      d = [2, 3, 5][rnd(0, 2)];
    } while (b <= d); // bezpiecznik: b - d > 0
    c = rnd(-6, 8);
    if (c === 0) c = 2;
  }

  // ---------------- PROGI (poprawny i "błędny") ----------------

  let t_num;
  let t_den;
  let wrong_num;
  let wrong_den;
  let direction; // "<=", "<", ">=", ">"

  if (variant === "Lminus") {
    // a - x (rel) (b x + c) / d
    // Poprawnie: d(a - x) rel b x + c
    // => da - dx rel b x + c
    // => -(b + d)x rel c - da
    // => (b + d)x rel' da - c   (rel' odwrócony)
    // => x rel'' (da - c)/(b + d)
    t_num = d * a - c;
    t_den = b + d;

    // Błąd: d(a - x) = da - x  (tylko a)
    // => da - x rel b x + c
    // => -(b + 1)x rel c - da
    // => (b + 1)x rel' da - c
    wrong_num = d * a - c;
    wrong_den = b + 1;

    // kierunek po przekształceniach (rel -> direction)
    switch (rel.key) {
      case ">=":
        direction = "<=";
        break;
      case ">":
        direction = "<";
        break;
      case "<=":
        direction = ">=";
        break;
      case "<":
        direction = ">";
        break;
      default:
        direction = "<=";
    }
  } else if (variant === "Rminus") {
    // x + a (rel) (c - b x) / d
    // Poprawnie: d(x + a) rel c - b x
    // => dx + da rel c - b x
    // => (b + d)x rel c - da
    // => x rel (c - da)/(b + d)
    t_num = c - d * a;
    t_den = b + d;

    // Błąd: d(x + a) = d c - b x  (skalujemy tylko c)
    // => dx + da rel d c - b x
    // => (b + d)x rel d(c - a)
    wrong_num = d * (c - a);
    wrong_den = b + d;

    direction = rel.key; // kierunek jak w relacji
  } else {
    // FracLeft: (b x + c) / d (rel) x + a
    // Poprawnie: b x + c rel d(x + a) = dx + da
    // => (b - d)x + c rel da
    // => (b - d)x rel da - c
    // => x rel (da - c)/(b - d)
    const k = b - d; // >0 z warunku b>d
    if (k === 0) return generateTask(); // awaryjny bezpiecznik
    t_num = d * a - c;
    t_den = k;

    // Błąd: d(x + a) = x + da  (tylko stała)
    // => b x + c rel x + da
    // => (b - 1)x + c rel da
    // => (b - 1)x rel da - c
    wrong_num = d * a - c;
    wrong_den = b - 1;

    direction = rel.key; // kierunek jak w relacji
  }

  // dodatkowy bezpiecznik – unikamy zerowego mianownika
  if (t_den === 0 || wrong_den === 0) {
    return generateTask();
  }

  const tLatex = mixedLatex(t_num, t_den);
  const tWrongLatex = mixedLatex(wrong_num, wrong_den);

  // ---------------- BUDOWANIE WYRAŻENIA NIERÓWNOŚCI ----------------

  let exprLatex;

  if (variant === "Lminus") {
    const cSign = c >= 0 ? "+ " : "- ";
    const cAbs = Math.abs(c);
    const rightNum = `${b}x ${cSign}${cAbs}`;
    exprLatex = `${a} - x \\; ${rel.latex} \\; \\dfrac{${rightNum}}{${d}}`;
  } else if (variant === "Rminus") {
    const bxPart = `${b}x`;
    exprLatex = `x + ${a} \\; ${rel.latex} \\; \\dfrac{${c} - ${bxPart}}{${d}}`;
  } else {
    // FracLeft
    const cSign = c >= 0 ? "+ " : "- ";
    const cAbs = Math.abs(c);
    const leftNum = `${b}x ${cSign}${cAbs}`;
    exprLatex = `\\dfrac{${leftNum}}{${d}} \\; ${rel.latex} \\; x + ${a}`;
  }

  // ---------------- ODPOWIEDZI – PRZEDZIAŁY ----------------

  const directionFlipped = flipDirection(direction);

  const correctInterval = intervalFromDirection(direction, tLatex);
  const wrongIntervalSameDir = intervalFromDirection(
    direction,
    tWrongLatex,
  );
  const wrongIntervalFlip = intervalFromDirection(
    directionFlipped,
    tLatex,
  );
  const wrongIntervalFlipWrong = intervalFromDirection(
    directionFlipped,
    tWrongLatex,
  );

  const rawOptions = [
    { latex: correctInterval, isCorrect: true },
    { latex: wrongIntervalSameDir, isCorrect: false },
    { latex: wrongIntervalFlip, isCorrect: false },
    { latex: wrongIntervalFlipWrong, isCorrect: false },
  ];

  const shuffled = [...rawOptions].sort(() => Math.random() - 0.5);
  const options = shuffled.map((opt, idx) => ({
    label: LETTERS[idx],
    latex: opt.latex,
    isCorrect: opt.isCorrect,
  }));
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLinearInequalityClosedTask",
    data: {
      variant,
      a,
      b,
      c,
      d,
      relKey: rel.key,
      relLatex: rel.latex,
      exprLatex,
      t_num,
      t_den,
      wrong_num,
      wrong_den,
      tLatex,
      tWrongLatex,
      direction,
      directionFlipped,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_7" },
  };
}

// ============================================================================
// RENDER TREŚCI
// ============================================================================
export function renderTask({ data }) {
  const { exprLatex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Zbiorem wszystkich rozwiązań nierówności{" "}
        <InlineMath math={exprLatex} /> jest przedział:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// INPUT (ABCD)
// ============================================================================
export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ============================================================================
// SPRAWDZANIE / ODP.
// ============================================================================
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ============================================================================
// WYJAŚNIENIE
// ============================================================================
export function renderExplanation({ data }) {
  const {
    variant,
    a,
    b,
    c,
    d,
    relKey,
    relLatex,
    t_num,
    t_den,
    tLatex,
    tWrongLatex,
    direction,
  } = data;

  const dirLatex = dirToLatex[direction] || "\\le";
  const intervalCorrect = intervalFromDirection(direction, tLatex);

  const cSign = c >= 0 ? "+ " : "- ";
  const cAbs = Math.abs(c);

  // raw ułamek progu (bez skrócenia)
  const fracRaw = String.raw`\dfrac{${t_num}}{${t_den}}`;
  const needSimpl = fracRaw !== tLatex;

  // --- Rachunek do postaci: (wsp)x ... (bez końcowego dzielenia na próg)
  let steps = "";

  if (variant === "Lminus") {
    const rightNum = `${b}x ${cSign}${cAbs}`;
    const flippedRel = flipDirection(relKey);
    const flippedRelLatex = dirToLatex[flippedRel] || relLatex;

    // ✅ bez nawiasów: -(b+d)x i (b+d)x
    steps = String.raw`\begin{aligned}
${a} - x\;&${relLatex}\;\dfrac{${rightNum}}{${d}}\\[6pt]
${d}(${a}-x)\;&${relLatex}\;${b}x ${cSign}${cAbs}\\[6pt]
${d * a} - ${d}x\;&${relLatex}\;${b}x ${cSign}${cAbs}\\[6pt]
-${d}x - ${b}x\;&${relLatex}\;${c} - ${d * a}\\[6pt]
-${b + d}x\;&${relLatex}\;${c - d * a}\\[6pt]
${b + d}x\;&${flippedRelLatex}\;${d * a - c}
\end{aligned}`;
  } else if (variant === "Rminus") {
    const bxPart = `${b}x`;

    // ✅ bez nawiasów: (b+d)x -> (b+d)x w ogóle bez nawiasu
    steps = String.raw`\begin{aligned}
x + ${a}\;&${relLatex}\;\dfrac{${c} - ${bxPart}}{${d}}\\[6pt]
${d}(x+${a})\;&${relLatex}\;${c} - ${b}x\\[6pt]
${d}x + ${d * a}\;&${relLatex}\;${c} - ${b}x\\[6pt]
${d}x + ${b}x\;&${relLatex}\;${c} - ${d * a}\\[6pt]
${b + d}x\;&${relLatex}\;${c - d * a}
\end{aligned}`;
  } else {
    const leftNum = `${b}x ${cSign}${cAbs}`;
    const k = b - d;

    // ✅ bez nawiasów: (k)x -> kx
    steps = String.raw`\begin{aligned}
\dfrac{${leftNum}}{${d}}\;&${relLatex}\;x + ${a}\\[6pt]
${b}x ${cSign}${cAbs}\;&${relLatex}\;${d}(x+${a})\\[6pt]
${b}x + ${c}\;&${relLatex}\;${d}x + ${d * a}\\[6pt]
${b}x - ${d}x\;&${relLatex}\;${d * a - c}\\[6pt]
${k}x\;&${relLatex}\;${d * a - c}
\end{aligned}`;
  }

  // --- Finalnie pokazujemy próg tylko raz:
  // raw -> (jeśli da się skrócić) -> po skróceniu
  const thresholdLines = needSimpl
    ? String.raw`\begin{aligned}
x\;&${dirLatex}\;${fracRaw}\\[4pt]
x\;&${dirLatex}\;${tLatex}
\end{aligned}`
    : String.raw`\begin{aligned}
x\;&${dirLatex}\;${tLatex}
\end{aligned}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Mnożymy obie strony przez dodatnią liczbę{" "}
        <InlineMath math={`${d}`} /> (znak nierówności się nie zmienia) i porządkujemy tak,
        aby otrzymać nierówność z samym <InlineMath math={"x"} />. Następnie (jeśli się da)
        skracamy ułamek w progu.
      </p>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={steps} />
        <BlockMath math={thresholdLines} />
      </div>

      <p>
        <strong>Krok 2.</strong> Zapisujemy rozwiązanie w postaci przedziału:
      </p>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x\\in\\; ${intervalCorrect}`} />
      </div>

      <p style={{ fontSize: 13, opacity: 0.85 }}>
        Uwaga: gdy w trakcie mnożenia lub porządkowania ktoś źle potraktuje wyraz z{" "}
        <InlineMath math={"x"} />, pojawi się inny próg, np.{" "}
        <InlineMath math={tWrongLatex} /> – dokładnie taki jak w błędnych odpowiedziach.
      </p>
    </div>
  );
}

